import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as randomUUID } from "uuid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Download } from "lucide-react";
import { PatientBillInfoDto, CreateMandateDtoSequenceTypeEnum } from "../../../../api-client";
import { useLocationId } from "../../../../stores/User/User.hooks";
import {
  useCreateSepaMandate,
  useGenerateSepaMandateForm,
  useUploadSepaMandateSignedDocument,
  useCreateSepaDirectDebit,
} from "../../../../stores/Sepa/sepa.mutations";
import { MandateStatusEnum, MandateSequenceTypeEnum } from "../../../../stores/Sepa/sepa.types";
import { useSepaBankAccounts } from "../../../../stores/Sepa/sepa.queries";
import BankAccountSelector, { BankSelection, emptyNewBank } from "./BankAccountSelector";
import { bankSelectionToDto, validateBankSelection } from "./bankSelection";
import { billChargeText, billTAccountId } from "./sepaBill";

interface Props {
  bill: PatientBillInfoDto;
  patientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the debit has been created (e.g. to close a surrounding modal). */
  onSuccess?: () => void;
}

type Step = "bank" | "sign" | "charge";
type MandateRef = { id: string; status: string };

const today = () => new Date().toISOString().slice(0, 10);

const formatEuro = (value: number) =>
  `${Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/** Groups an IBAN into blocks of four for display. */
const formatIban = (iban?: string | null) => (iban || "").replace(/(.{4})/g, "$1 ").trim();

/**
 * One-off direct debit as a guided flow (SEPA-compliant): create a single-use
 * (OOFF) authorisation, print it for signing, upload the signed copy with the
 * ACTUAL signature date and an explicit confirmation to activate it, and only
 * then collect the payment.
 */
const EinmalLastschriftDialog: React.FC<Props> = ({
  bill,
  patientId,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const locationId = useLocationId();
  const createMandate = useCreateSepaMandate();
  const generateForm = useGenerateSepaMandateForm();
  const uploadSigned = useUploadSepaMandateSignedDocument(patientId);
  const charge = useCreateSepaDirectDebit();
  const { data: bankAccounts = [] } = useSepaBankAccounts(patientId, open);

  const amount = Number(bill.dueAmount ?? 0);

  const [step, setStep] = useState<Step>("bank");
  const [bank, setBank] = useState<BankSelection>(emptyNewBank);
  const [mandate, setMandate] = useState<MandateRef | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Signing step inputs.
  const [signFile, setSignFile] = useState<File | null>(null);
  const [signatureDate, setSignatureDate] = useState<string>(today());
  const [confirmed, setConfirmed] = useState(false);

  // The one-off authorisation for THIS prescription that was started earlier and is
  // still pending signature (DRAFT) or signed but not yet charged (ACTIVE).
  const pendingMandates = useMemo(
    () =>
      bankAccounts
        .flatMap((a) => a.mandates)
        .filter(
          (m) =>
            m.sequenceType === MandateSequenceTypeEnum.Ooff &&
            m.rxId === bill.rxId &&
            (m.status === MandateStatusEnum.Active || m.status === MandateStatusEnum.Draft),
        ),
    [bankAccounts, bill.rxId],
  );

  // Holder/IBAN/reference for the current mandate (once created/resumed), resolved
  // from the loaded bank accounts so the sign/charge steps can show it.
  const mandateInfo = useMemo(() => {
    if (!mandate) return null;
    for (const acc of bankAccounts) {
      const m = acc.mandates.find((mm) => mm.id === mandate.id);
      if (m) {
        return {
          reference: m.mandateReference ?? mandate.id,
          holder: `${acc.holderFirstName ?? ""} ${acc.holderLastName ?? ""}`.trim(),
          iban: acc.iban,
          bic: acc.bic ?? "",
        };
      }
    }
    return null;
  }, [mandate, bankAccounts]);

  // One idempotency key per open = one debit intent, reused across retries.
  const idempotencyKeyRef = useRef<string>(randomUUID());
  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current = randomUUID();
      setStep("bank");
      setBank(emptyNewBank);
      setMandate(null);
      setErrors({});
      setSignFile(null);
      setSignatureDate(today());
      setConfirmed(false);
    }
  }, [open]);

  const busy =
    createMandate.isPending || generateForm.isPending || uploadSigned.isPending || charge.isPending;

  // Resume the pending one-off for this prescription: jump to the upload step (DRAFT)
  // or the pay step (ACTIVE) instead of starting a new one from the bank step.
  useEffect(() => {
    if (!open || mandate || step !== "bank" || pendingMandates.length !== 1) return;
    const only = pendingMandates[0];
    setMandate({ id: only.id, status: only.status });
    setStep(only.status === MandateStatusEnum.Active ? "charge" : "sign");
  }, [open, pendingMandates, mandate, step]);

  const openForm = async (mandateId: string) => {
    const doc = await generateForm.mutateAsync(mandateId);
    if (doc?.accessUrl) window.open(doc.accessUrl, "_blank");
  };

  /** Collects the payment against the (now active) mandate, then closes. */
  const chargeMandate = async (mandateId: string) => {
    await charge.mutateAsync({
      patientId,
      accountId: bill.accountId,
      mandateId,
      amount,
      text: `${billChargeText(bill)} - Patient: ${patientId}`,
      rxId: bill.rxId,
      tAccountId: billTAccountId(bill),
      businessId: bill.businessId,
      locationId,
      idempotencyKey: idempotencyKeyRef.current,
    });
    onOpenChange(false);
    onSuccess?.();
  };

  /** Step 1: create the OOFF authorisation, then print it (or resume if already active). */
  const handleCreateAuthorization = async () => {
    const nextErrors = validateBankSelection(bank);
    if (!amount || amount <= 0) nextErrors.amount = "Betrag muss positiv sein";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const created = (await createMandate.mutateAsync({
      patientId,
      accountId: bill.accountId,
      rxId: bill.rxId,
      sequenceType: CreateMandateDtoSequenceTypeEnum.Ooff,
      maxAmount: amount,
      ...bankSelectionToDto(bank),
    })) as MandateRef;
    setMandate(created);

    if (created.status === MandateStatusEnum.Active) {
      setStep("charge");
      return;
    }
    await openForm(created.id);
    setStep("sign");
  };

  /** Step 2: upload the signed authorisation + confirmation to activate the mandate. */
  const handleActivate = async () => {
    if (!mandate) return;
    const nextErrors: Record<string, string> = {};
    if (!signFile) nextErrors.file = "Bitte das signierte Dokument auswählen";
    if (!signatureDate) nextErrors.signatureDate = "Unterschriftsdatum erforderlich";
    else if (signatureDate > today()) nextErrors.signatureDate = "Darf nicht in der Zukunft liegen";
    if (!confirmed) nextErrors.confirmed = "Bitte bestätigen";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !signFile) return;

    await uploadSigned.mutateAsync({ id: mandate.id, file: signFile, signatureDate, confirmed });
    // For a one-off, activation and collection are the same intent: charge now.
    await chargeMandate(mandate.id);
  };

  const handleCharge = () => {
    if (mandate) void chargeMandate(mandate.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Einmal-Lastschrift</DialogTitle>
          <DialogDescription className="text-[15px]">
            Einmalige Lastschrift für Rezept {bill.rxNumber} über {formatEuro(amount)}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1">
            <Label className="text-base">Betrag</Label>
            <p className="text-lg font-semibold text-slate-800">{formatEuro(amount)}</p>
          </div>

          {mandateInfo && step !== "bank" && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-solid border-slate-200 bg-slate-50/60 p-3 text-sm">
              <dt className="font-medium text-slate-500">Mandatsreferenz</dt>
              <dd className="font-semibold text-slate-800">{mandateInfo.reference}</dd>
              <dt className="font-medium text-slate-500">Kontoinhaber</dt>
              <dd className="text-slate-800">{mandateInfo.holder || "—"}</dd>
              <dt className="font-medium text-slate-500">IBAN</dt>
              <dd className="font-mono text-slate-800">{formatIban(mandateInfo.iban)}</dd>
              {mandateInfo.bic && (
                <>
                  <dt className="font-medium text-slate-500">BIC</dt>
                  <dd className="text-slate-800">{mandateInfo.bic}</dd>
                </>
              )}
            </dl>
          )}

          {step === "bank" && (
            <BankAccountSelector
              patientId={patientId}
              value={bank}
              onChange={setBank}
              errors={errors}
              disabled={busy}
            />
          )}

          {step === "sign" && (
            <div className="space-y-5">
              <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                Autorisierung erstellt. Bitte unterschreiben lassen, das signierte Dokument
                hochladen und die Prüfung bestätigen. Danach wird die Lastschrift eingezogen.
              </p>
              <Button
                variant="secondary"
                className="gap-2"
                disabled={busy}
                onClick={() => mandate && openForm(mandate.id)}
              >
                <Download className="h-4 w-4" />
                Autorisierung erneut herunterladen
              </Button>

              <div className="space-y-1.5">
                <Label className="text-base">Signiertes Dokument</Label>
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  disabled={busy}
                  onChange={(e) => setSignFile(e.target.files?.[0] ?? null)}
                />
                {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-base">Unterschrieben am</Label>
                <Input
                  type="date"
                  max={today()}
                  value={signatureDate}
                  disabled={busy}
                  onChange={(e) => setSignatureDate(e.target.value)}
                />
                {errors.signatureDate && (
                  <p className="text-sm text-destructive">{errors.signatureDate}</p>
                )}
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-solid border-slate-200 p-3">
                <Checkbox
                  checked={confirmed}
                  disabled={busy}
                  onCheckedChange={(v) => setConfirmed(v === true)}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700">
                  Ich bestätige, dass das Dokument lesbar und unterschrieben ist und den
                  eingegebenen Bank- und Schuldnerdaten entspricht.
                </span>
              </label>
              {errors.confirmed && <p className="text-sm text-destructive">{errors.confirmed}</p>}
            </div>
          )}

          {step === "charge" && (
            <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
              Mandat aktiv. Die Lastschrift kann jetzt eingezogen werden.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          {step === "bank" && (
            <Button
              className="bg-blue-darkest text-white hover:bg-blue-darkest/90"
              onClick={handleCreateAuthorization}
              disabled={busy}
            >
              {busy ? "Wird erstellt…" : "Autorisierung erstellen"}
            </Button>
          )}
          {step === "sign" && (
            <Button
              className="bg-blue-darkest text-white hover:bg-blue-darkest/90"
              onClick={handleActivate}
              disabled={busy}
            >
              {busy ? "Wird verarbeitet…" : "Aktivieren & Einziehen"}
            </Button>
          )}
          {step === "charge" && (
            <Button
              className="bg-blue-darkest text-white hover:bg-blue-darkest/90"
              onClick={handleCharge}
              disabled={busy}
            >
              {busy ? "Wird eingezogen…" : "Einziehen"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EinmalLastschriftDialog;
